import React, { useEffect, useState } from 'react';
import { CleverClassroom, CleverStudent, getCleverClassroomStudents } from '../services/CleverStudentImport.service';
import Loader from '../../../components/Common/Loader';
import Checkbox from '../../../components/Common/Checkbox';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import BackButton from '../../../components/Common/BackButton';

interface CleverStudentSelectorProps {
  classroom: CleverClassroom;
  onStudentsSelect: (studentIds: string[]) => void;
  onBack: () => void;
  onClose: () => void;
  cleverImportLoading?: boolean;
}

const CleverStudentSelector: React.FC<CleverStudentSelectorProps> = ({
  classroom,
  onStudentsSelect,
  onBack,
  onClose,
  cleverImportLoading,
}) => {
  const activeClassroom = useSelector((state: RootState) => state.user.activeClassroom);

  const [students, setStudents] = useState<CleverStudent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setLoading(true);
        const data = await getCleverClassroomStudents(classroom.id, activeClassroom?.id || '');

        if (data) {
          setStudents(data);
        } else {
          setError('Failed to fetch students from Clever');
        }
      } catch (error) {
        setError('Failed to fetch students from Clever');
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, [classroom.id]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedStudents(new Set(students.map((student) => student.id)));
      setSelectAll(true);
    } else {
      setSelectedStudents(new Set());
      setSelectAll(false);
    }
  };

  const handleStudentSelect = (studentId: string, checked: boolean) => {
    const newSelected = new Set(selectedStudents);
    if (checked) {
      newSelected.add(studentId);
    } else {
      newSelected.delete(studentId);
    }
    setSelectedStudents(newSelected);
    setSelectAll(newSelected.size === students.length);
  };

  const handleImport = () => {
    onStudentsSelect(Array.from(selectedStudents));
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-8 md:min-w-80 font-sofiaSans">
        <Loader loader={true} dimentions="w-8 h-8" />
        <p className="mt-4 text-gray-600">Loading students from Clever...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-8 md:min-w-80 font-sofiaSans">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={onBack}
          className="px-6 py-2 h-9 bg-secondaryStoryAdditionalLightBlueText text-whiteText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center justify-center text-sm sm:text-base w-full sm:w-auto"
        >
          Back
        </button>
      </div>
    );
  }

  if (students.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 md:min-w-80 font-sofiaSans">
        <p className="text-gray-600 mb-4">No students found in this classroom.</p>
        <button
          onClick={onBack}
          className="px-6 py-2 h-9 bg-secondaryStoryAdditionalLightBlueText text-whiteText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center justify-center text-sm sm:text-base w-full sm:w-auto"
        >
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:min-w-80 font-sofiaSans">
      <div className="text-left">
        <h3 className="w-full flex justify-start text-left text-xl md:text-xl mb-4 font-bold text-storyblue main-title-modal-text">
          Select students to import
        </h3>
      </div>

      <p className="text-sm">
        In addition to signing in with Clever, Build My Story log-ins have been auto-generated for your students as an
        additional log-in option for home devices.
      </p>

      <div className="space-y-2 max-h-64 overflow-y-auto">
        {/* Select All Option */}
        <div className="flex items-center p-3 border border-gray-200 rounded-lg bg-gray-50">
          <Checkbox
            checked={selectAll}
            onCheckedChange={handleSelectAll}
            title="Select all students"
            backgroundColor="[#d9d9d9]"
            checkedBackgroundColor="secondaryStoryAdditionalLightBlueText"
            size={5}
          />
          <span className="ml-3 font-medium">Select All ({students.length})</span>
        </div>

        {/* Individual Students */}
        {students.map((student) => (
          <div key={student.id} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
            <Checkbox
              checked={selectedStudents.has(student.id)}
              onCheckedChange={(checked) => handleStudentSelect(student.id, checked)}
              title={`Select ${student.name.first} ${student.name.last}`}
              backgroundColor="[#d9d9d9]"
              checkedBackgroundColor="secondaryStoryAdditionalLightBlueText"
              size={5}
            />
            <div className="ml-3 flex-1">
              <span className="font-medium">
                {student.name.first} {student.name.last}
              </span>
              {student.grade && <span className="text-sm text-gray-600 ml-2">Grade {student.grade}</span>}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-between pt-4">
        <div>
          <BackButton onClick={onBack}></BackButton>
        </div>
        <button
          onClick={handleImport}
          disabled={selectedStudents.size === 0}
          className={`px-6 py-2 h-9  font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center justify-center text-sm sm:text-base w-full sm:w-auto ${
            selectedStudents.size > 0
              ? 'bg-secondaryStoryAdditionalLightBlueText text-whiteText'
              : 'opacity-50 cursor-not-allowed'
          }`}
        >
          Import ({selectedStudents.size})
          <Loader loader={cleverImportLoading} dimentions="w-8 h-8" />
        </button>
      </div>
    </div>
  );
};

export default CleverStudentSelector;
